/**
 * If `value` is a string holding a JSON object or array, return the parsed structure;
 * otherwise return undefined.
 *
 * Only objects/arrays are parsed — a string that parses as a bare JSON scalar ("42",
 * "true", '"x"') is left alone, since those gain nothing from tree rendering and parsing
 * them would silently retype a literal string into a number/boolean. The leading-character
 * guard keeps this cheap: ordinary strings never reach JSON.parse.
 */
function tryParseJsonContainer(value: string): unknown {
  const trimmed = value.trim();
  if (trimmed[0] !== "{" && trimmed[0] !== "[") {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return parsed !== null && typeof parsed === "object" ? parsed : undefined;
  } catch {
    return undefined; // looks like JSON but isn't — keep the literal string
  }
}

/**
 * Prepare message data for react-json-view:
 * - Convert bigints to strings (JSON-incompatible).
 * - Expand a string that contains a JSON object or array into that structure, so
 *   react-json-view renders it as a navigable, collapsible tree rather than one opaque
 *   quoted string. Strings that aren't a JSON object/array are passed through unchanged.
 *   This is what lets columns stored as JSON text (e.g. EVTX EventData/UserData) read as
 *   trees in the Raw Panel while remaining plain strings everywhere else.
 */
export function sanitizeForJsonView(obj: unknown): Record<string, unknown> {
  function walk(value: unknown): unknown {
    if (typeof value === "bigint") {
      return value.toString();
    } else if (Array.isArray(value)) {
      return value.map((item) => walk(item));
    } else if (value !== null && typeof value === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) {
        out[k] = walk(v);
      }
      return out;
    } else if (typeof value === "string") {
      // Expand embedded JSON; a non-JSON string returns undefined and is kept as-is.
      const parsed = tryParseJsonContainer(value);
      return parsed === undefined ? value : walk(parsed);
    }
    return value;
  }

  return walk(obj) as Record<string, unknown>;
}
